/**
 * ForgotPasswordPage — public page at /forgot-password
 *
 * Two render states:
 *   1. form   — hospital slug + email inputs
 *   2. sent   — neutral "check your email" confirmation (regardless of match)
 *
 * Never reveals whether the email matched — always shows the neutral state.
 *
 * Design: reuses clinical-card shell from LoginPage.tsx
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

type PageState = 'form' | 'submitting' | 'sent';

export function ForgotPasswordPage() {
  const [pageState, setPageState] = useState<PageState>('form');
  const [hospitalSlug, setHospitalSlug] = useState('');
  const [email, setEmail] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);

  // ---------------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------------

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFieldError(null);

    const slug = hospitalSlug.trim().toLowerCase();
    const trimmedEmail = email.trim().toLowerCase();

    if (!slug) {
      setFieldError('Please enter your hospital code.');
      return;
    }
    if (!trimmedEmail || !trimmedEmail.includes('@')) {
      setFieldError('Please enter a valid email address.');
      return;
    }

    setPageState('submitting');
    try {
      await api.post('/api/auth/forgot-password', {
        email: trimmedEmail,
        hospitalSlug: slug,
      });
    } catch {
      // intentionally swallow — always show neutral state
    }
    setPageState('sent');
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="min-h-screen bg-[var(--color-bg)] flex flex-col items-center justify-center px-4 py-12">
      <div className="w-full max-w-sm">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-full bg-clinical-charcoal flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-7 h-7 text-white"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75"
              />
            </svg>
          </div>
          <h1 className="font-heading text-2xl font-semibold text-clinical-charcoal leading-tight">
            {pageState === 'sent' ? 'Check your email' : 'Forgot password?'}
          </h1>
          <p className="text-sm font-body text-clinical-text-muted mt-1">
            {pageState === 'sent'
              ? "If an account exists for that email, we've sent a reset link."
              : 'Enter your hospital code and email address.'}
          </p>
        </div>

        {/* ---- Sent state ---- */}
        {pageState === 'sent' && (
          <div className="clinical-card p-8 text-center">
            <div className="w-12 h-12 rounded-full bg-teal-50 flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-6 h-6 text-clinical-teal"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                />
              </svg>
            </div>
            <p className="text-sm font-body text-clinical-charcoal mb-2">
              The link expires in <strong>24 hours</strong> and can only be used once.
            </p>
            <p className="text-sm font-body text-clinical-text-muted mb-6">
              If you don't receive an email within a few minutes, check your spam folder or contact
              your hospital administrator.
            </p>
            <Link
              to="/login"
              className="text-sm font-body text-clinical-teal hover:underline"
            >
              Back to sign in
            </Link>
          </div>
        )}

        {/* ---- Form ---- */}
        {(pageState === 'form' || pageState === 'submitting') && (
          <div className="clinical-card p-6">
            <form onSubmit={handleSubmit} noValidate className="space-y-5">
              {/* Hospital code */}
              <div>
                <label htmlFor="fp-hospital-slug" className="form-label">
                  Hospital code
                </label>
                <input
                  id="fp-hospital-slug"
                  type="text"
                  autoComplete="organization"
                  required
                  value={hospitalSlug}
                  onChange={(e) => {
                    setHospitalSlug(e.target.value);
                    if (fieldError) setFieldError(null);
                  }}
                  className="form-input"
                  placeholder="e.g. king-faisal"
                  disabled={pageState === 'submitting'}
                />
                <p className="text-xs font-body text-clinical-text-muted mt-1.5">
                  The code your administrator provided when setting up your account.
                </p>
              </div>

              {/* Email */}
              <div>
                <label htmlFor="fp-email" className="form-label">
                  Email address
                </label>
                <input
                  id="fp-email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (fieldError) setFieldError(null);
                  }}
                  className="form-input"
                  placeholder="you@hospital.com"
                  disabled={pageState === 'submitting'}
                />
              </div>

              {/* Field error */}
              {fieldError && (
                <div
                  role="alert"
                  className="p-3 rounded-clinical bg-red-50 border border-red-200 text-sm font-body text-red-700"
                >
                  {fieldError}
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={
                  hospitalSlug.trim().length === 0 ||
                  email.trim().length === 0 ||
                  pageState === 'submitting'
                }
                className="btn-primary w-full"
              >
                {pageState === 'submitting' ? (
                  <>
                    <span
                      className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"
                      aria-hidden="true"
                    />
                    Sending...
                  </>
                ) : (
                  'Send reset link'
                )}
              </button>
            </form>
          </div>
        )}

        {/* Back link */}
        {(pageState === 'form' || pageState === 'submitting') && (
          <p className="text-center text-sm font-body text-clinical-text-muted mt-6">
            <Link
              to="/login"
              className="text-clinical-teal hover:text-clinical-teal-dark underline-offset-2 hover:underline transition-colors"
            >
              Back to sign in
            </Link>
          </p>
        )}
      </div>
    </div>
  );
}
